// Link helpers. Each returns a URL that opens the respective native app
// on tap: tel: (dialer), mailto: (mail), Google Maps (maps), https (browser).

export const enc = (s?: string): string => encodeURIComponent(s ?? '');

export const telHref = (phone?: string): string =>
  'tel:' + String(phone ?? '').replace(/[^+\d]/g, '');

export const mailHref = (email?: string): string => 'mailto:' + (email ?? '');

export const mapHref = (address?: string): string =>
  'https://www.google.com/maps/search/?api=1&query=' + enc(address);

export const urlHref = (url?: string): string =>
  !url ? '#' : (/^https?:\/\//.test(url) ? url : 'https://' + url);

export const urlLabel = (url?: string): string =>
  String(url ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '');

/**
 * What a back-of-card row actually IS, decided by its value.
 *
 * The back's labelled links hold whatever had no slot on the front — a second
 * address, another site, a fax — so a single link glyph on every row said
 * "web link" about a street address. The value decides, not a stored kind:
 * extras arrive from OCR and from free text, and a model's `kind` is the least
 * reliable part of either (a card read as a PDF once classified a NAME as a
 * title).
 */
export type RowKind = 'email' | 'phone' | 'website' | 'address';

const EMAILISH = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONEISH = /^[+\d][\d\s\-()./]{5,}$/;
// A host with a dot and no spaces — "nexus.io", "linkedin.com/in/alex".
const URLISH = /^(https?:\/\/)?[^\s/]+\.[a-z]{2,}(\/\S*)?$/i;

export const rowKind = (value?: string): RowKind => {
  const v = String(value ?? '').trim();
  if (EMAILISH.test(v)) return 'email';
  if (PHONEISH.test(v)) return 'phone';
  if (URLISH.test(v)) return 'website';
  return 'address';
};

/** The icon name for a back row — mirrors the front's glyph for that kind. */
export const rowIcon = (value?: string): 'mail' | 'call' | 'language' | 'place' =>
  ({ email: 'mail', phone: 'call', website: 'language', address: 'place' } as const)[rowKind(value)];

/** Where a back row should go on tap: the dialer, mail, maps, or the browser. */
export const rowHref = (value?: string): string => {
  switch (rowKind(value)) {
    case 'email': return mailHref(value);
    case 'phone': return telHref(value);
    case 'address': return mapHref(value);
    default: return urlHref(value);
  }
};

/** Display name for a social network key (the server's closed allow-list). */
export const socialLabel = (key: string): string =>
  ({ linkedin: 'LinkedIn', github: 'GitHub', x: 'X', instagram: 'Instagram',
    youtube: 'YouTube', facebook: 'Facebook', tiktok: 'TikTok', threads: 'Threads',
    mastodon: 'Mastodon', telegram: 'Telegram', whatsapp: 'WhatsApp',
    calendly: 'Calendly', substack: 'Substack', dribbble: 'Dribbble',
    behance: 'Behance', website: 'Website', imo: 'imo', viber: 'Viber',
    messenger: 'Messenger', wechat: 'WeChat', line: 'LINE', signal: 'Signal',
    snapchat: 'Snapchat', pinterest: 'Pinterest', twitch: 'Twitch',
    discord: 'Discord', reddit: 'Reddit', medium: 'Medium', upwork: 'Upwork',
    fiverr: 'Fiverr', linktree: 'Linktree' } as Record<string, string>)[key]
  ?? key.charAt(0).toUpperCase() + key.slice(1);
